import React, { useState } from "react";
import PropTypes from "prop-types";

import { Box, Paper, Tab, Tabs, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LocalDiningOutlinedIcon from "@mui/icons-material/LocalDiningOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import { useSelector } from "react-redux";

import OnlineCheckoutSettings from "./OnlineCheckoutSettings";
import FirstPageDialogSettings from "./FirstPageDialogSettings";
import PaymentRequiredQRCodeTable from "./PaymentReqiredQRCodes";

import CheckMyRestaurant from "../../CheckMyRestaurant";
import SettingsHeader from "../SettingsHeader";

// pane === "welcome" | "checkout" | "prepay"
// checkoutSection: number 0..5 passed to OnlineCheckoutSettings.activeSection
const TABS = [
  {
    key: "welcome",
    label: "Welcome dialog",
    hint: "Home screen greeting",
    icon: <ChatBubbleOutlineIcon fontSize="small" />,
    pane: "welcome",
  },
  {
    key: "dinein",
    label: "Dine-in",
    hint: "Checkout requirements",
    icon: <RestaurantMenuOutlinedIcon fontSize="small" />,
    pane: "checkout",
    checkoutSection: 0,
  },
  {
    key: "foodcourt",
    label: "Food court",
    hint: "Payment & takeaway",
    icon: <StorefrontOutlinedIcon fontSize="small" />,
    pane: "checkout",
    checkoutSection: 1,
  },
  {
    key: "delivery",
    label: "Delivery",
    hint: "ETA & cash on delivery",
    icon: <DeliveryDiningOutlinedIcon fontSize="small" />,
    pane: "checkout",
    checkoutSection: 6,
  },
  {
    key: "guest",
    label: "Guest experience",
    hint: "Storefront options",
    icon: <LocalDiningOutlinedIcon fontSize="small" />,
    pane: "checkout",
    checkoutSection: 2,
  },
  {
    key: "payments",
    label: "Payments",
    hint: "Providers & surcharges",
    icon: <PaymentsOutlinedIcon fontSize="small" />,
    pane: "checkout",
    checkoutSection: 3,
  },
  {
    key: "legal",
    label: "Notices & terms",
    hint: "Allergy & legal copy",
    icon: <GavelOutlinedIcon fontSize="small" />,
    pane: "checkout",
    checkoutSection: 4,
  },
  {
    key: "completion",
    label: "Order completion",
    hint: "After-order rules",
    icon: <DoneAllOutlinedIcon fontSize="small" />,
    pane: "checkout",
    checkoutSection: 5,
  },
  {
    key: "prepay",
    label: "Prepay tables",
    hint: "Payment required tables",
    icon: <TableRestaurantIcon fontSize="small" />,
    pane: "prepay",
  },
];

const useStyles = makeStyles((theme) => ({
  shell: {
    display: "flex",
    gap: theme.spacing(3),
    alignItems: "flex-start",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      gap: theme.spacing(2),
    },
  },
  rail: {
    width: 280,
    flexShrink: 0,
    position: "sticky",
    top: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    borderRadius: theme.shape.borderRadius * 2,
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      position: "static",
      padding: 0,
    },
  },
  verticalTabs: {
    "& .MuiTabs-indicator": {
      left: 0,
      right: "auto",
      width: 3,
      borderRadius: 2,
    },
  },
  horizontalTabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: 2,
    },
  },
  tab: {
    minHeight: 60,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    textAlign: "left",
    textTransform: "none",
    padding: theme.spacing(1.25, 2.5),
    fontWeight: 500,
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.selected,
      fontWeight: 600,
    },
  },
  tabRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  iconSlot: {
    width: 24,
    height: 24,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    "& > svg": {
      fontSize: 20,
    },
  },
  tabHint: {
    display: "block",
    fontSize: "0.72rem",
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginTop: 2,
    lineHeight: 1.3,
  },
  tabInner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 0,
    flex: 1,
  },
  pane: {
    flex: 1,
    minWidth: 0,
  },
}));

const OnlineSettings = (props) => {
  const { className } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const restaurant = useSelector(
    (state) => state.restaurant.selectedRestaurant
  );

  if (!restaurant) {
    return <CheckMyRestaurant />;
  }

  const handleChange = (_event, newValue) => setActiveTab(newValue);

  const renderTabLabel = (tab) => (
    <Box className={classes.tabRow}>
      <Box className={classes.iconSlot}>{tab.icon}</Box>
      <Box className={classes.tabInner}>
        <span>{tab.label}</span>
        {!isCompact && <span className={classes.tabHint}>{tab.hint}</span>}
      </Box>
    </Box>
  );

  const activeTabConfig = TABS[activeTab];

  const renderPane = () => {
    if (activeTabConfig.pane === "welcome") {
      return <FirstPageDialogSettings />;
    }
    if (activeTabConfig.pane === "prepay") {
      return <PaymentRequiredQRCodeTable />;
    }
    return (
      <OnlineCheckoutSettings activeSection={activeTabConfig.checkoutSection} />
    );
  };

  return (
    <Box className={className}>
      <SettingsHeader
        eyebrow="Settings · Online"
        title="Online Ordering"
        subtitle="Control the welcome dialog, checkout behaviour, payment providers and prepay tables that shape the customer-facing ordering experience."
      />

      <Box className={classes.shell}>
        <Paper elevation={0} variant="outlined" className={classes.rail}>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            orientation={isCompact ? "horizontal" : "vertical"}
            variant={isCompact ? "scrollable" : "standard"}
            scrollButtons={isCompact ? "auto" : false}
            allowScrollButtonsMobile
            aria-label="Online settings sections"
            className={
              isCompact ? classes.horizontalTabs : classes.verticalTabs
            }
          >
            {TABS.map((tab, index) => (
              <Tab
                key={tab.key}
                label={renderTabLabel(tab)}
                className={classes.tab}
                id={`settings-tab-${index}`}
                aria-controls={`settings-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Paper>

        <Box
          className={classes.pane}
          role="tabpanel"
          id={`settings-tabpanel-${activeTab}`}
          aria-labelledby={`settings-tab-${activeTab}`}
        >
          {renderPane()}
        </Box>
      </Box>
    </Box>
  );
};

OnlineSettings.propTypes = {
  className: PropTypes.string,
};

export default OnlineSettings;
